import { useEffect, useRef, useState } from "react";

import "./messagingWindow.css";

// Import children components to render.
import Conversation from "../components/conversation";

// Phone breakpoint — must match the messagingWindow.css sheet media query.
// Tablets and up keep the desktop floating-card experience.
const MOBILE_SHEET_QUERY = "(max-width: 640px)";

export default function MessagingWindow(props) {
    let [uiReady, setUIReady] = useState(false);
    // AWAC: true while the gallery wing is open. Drives the horizontal expansion.
    let [galleryActive, setGalleryActive] = useState(false);
    // Fullscreen toggle: the floating card grows to (nearly) the whole viewport.
    let [isFullscreen, setIsFullscreen] = useState(false);
    const windowRef = useRef(null);

    // Phones: pin the full-bleed sheet to the VISUAL viewport while the
    // on-screen keyboard is open. iOS never resizes the layout viewport for
    // the keyboard (100dvh stays the same), so without this the input row
    // ends up hidden behind it. Sets --awac-vvh/--awac-vvt consumed by the
    // max-width:480px rules; both are cleared when the keyboard goes away.
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) {
            return undefined;
        }
        const mq = window.matchMedia(MOBILE_SHEET_QUERY);
        const apply = () => {
            const el = windowRef.current;
            if (!el) {
                return;
            }
            const keyboardDelta = window.innerHeight - vv.height;
            if (mq.matches && (keyboardDelta > 50 || vv.offsetTop > 0)) {
                el.style.setProperty("--awac-vvh", Math.round(vv.height) + "px");
                el.style.setProperty("--awac-vvt", Math.round(vv.offsetTop) + "px");
                // Keyboard open: the sheet fills the visual viewport so the
                // composer always stays visible (CSS .awacKeyboard rules).
                el.classList.add("awacKeyboard");
            } else {
                el.style.removeProperty("--awac-vvh");
                el.style.removeProperty("--awac-vvt");
                el.classList.remove("awacKeyboard");
            }
        };
        vv.addEventListener("resize", apply);
        vv.addEventListener("scroll", apply);
        apply();
        return () => {
            vv.removeEventListener("resize", apply);
            vv.removeEventListener("scroll", apply);
        };
    }, []);

    // Phones: lock the page behind the open chat sheet (html.awacChatOpenMobile
    // -> overflow:hidden), so iOS can't rubber-band the site underneath while
    // the visitor scrolls the conversation. Re-evaluated when the chat is
    // minimized/restored and when the viewport crosses the phone breakpoint.
    useEffect(() => {
        const mq = window.matchMedia(MOBILE_SHEET_QUERY);
        const apply = () => {
            const lock = mq.matches && uiReady && !props.isMinimized;
            document.documentElement.classList.toggle("awacChatOpenMobile", lock);
        };
        apply();
        if (mq.addEventListener) {
            mq.addEventListener("change", apply);
        }
        return () => {
            if (mq.removeEventListener) {
                mq.removeEventListener("change", apply);
            }
            document.documentElement.classList.remove("awacChatOpenMobile");
        };
    }, [uiReady, props.isMinimized]);

    /**
     * Sets the app's ui ready status based on the communication from the child Conversation component..
     * Propogates the same upto the parent BootstrapMessaging component for Messaging Button reactive ui updates.
     * @param {boolean}
     */
    function setAppUIReady(isUIReady) {
        setUIReady(isUIReady);
        props.deactivateMessagingButton(isUIReady);
    }

    /**
     * Generates a classname for the parent div that holds the messaging window ui.
     * Hides the parent div if the app is not ui ready.
     * @returns {string}
     */
    function generateMessagingWindowClassName() {
        const className = "messagingWindow";

        // AWAC: when the gallery wing is open, the widget expands horizontally
        // into a two-column layout (chat + gallery) inside ONE container.
        // Phones: awacFullscreen doubles as the sheet's FULL (94dvh) snap —
        // the same header button toggles maximize on desktop and expand/
        // collapse on the sheet.
        return className
            + `${uiReady ? "" : " hide"}`
            + `${props.isMinimized ? " awacMinimized" : ""}`
            + `${galleryActive ? " awacExpanded" : ""}`
            + `${isFullscreen ? " awacFullscreen" : ""}`
            + `${dragging ? " awacDragging" : ""}`;
    }

    // ── Phone sheet drag (design handoff §4) ──────────────────────────────
    // Pointer-captured drag on the grabber resizes the sheet live (no
    // transition); release snaps: >76% of the viewport → full, <38% → closed
    // (minimize), else half. Desktop never renders the grabber.
    const [dragging, setDragging] = useState(false);
    const dragRef = useRef(null);

    function onGrabberPointerDown(e) {
        const el = windowRef.current;
        if (!el) {
            return;
        }
        dragRef.current = { startY: e.clientY, startH: el.getBoundingClientRect().height };
        setDragging(true);
        e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId);
    }

    function onGrabberPointerMove(e) {
        const el = windowRef.current;
        if (!el || !dragRef.current) {
            return;
        }
        const h = Math.max(120, dragRef.current.startH + (dragRef.current.startY - e.clientY));
        el.style.height = h + "px";
    }

    function onGrabberPointerUp() {
        const el = windowRef.current;
        if (!el || !dragRef.current) {
            return;
        }
        const viewportH = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
        const pct = el.getBoundingClientRect().height / viewportH;
        el.style.removeProperty("height");
        dragRef.current = null;
        setDragging(false);
        if (pct > 0.76) {
            setIsFullscreen(true);
        } else if (pct < 0.38) {
            setIsFullscreen(false);
            props.minimizeMessagingWindow();
        } else {
            setIsFullscreen(false);
        }
    }

    return(
        <>
            {!props.isMinimized && uiReady &&
                <div
                    className="awacSheetScrim"
                    onClick={props.minimizeMessagingWindow}
                    aria-hidden="true" />
            }
            <div ref={windowRef} className={generateMessagingWindowClassName()} aria-hidden={props.isMinimized ? "true" : "false"}>
                <div
                    className="awacSheetGrabber"
                    onPointerDown={onGrabberPointerDown}
                    onPointerMove={onGrabberPointerMove}
                    onPointerUp={onGrabberPointerUp}
                    onPointerCancel={onGrabberPointerUp}
                    aria-hidden="true" />
                <Conversation
                    isExistingConversation={props.isExistingConversation}
                    showMessagingWindow={props.showMessagingWindow}
                    minimizeMessagingWindow={props.minimizeMessagingWindow}
                    onGalleryActiveChange={setGalleryActive}
                    isFullscreen={isFullscreen}
                    onToggleFullscreen={() => setIsFullscreen((v) => !v)}
                    exitFullscreen={() => setIsFullscreen(false)}
                    uiReady={setAppUIReady} />
            </div>
        </>
    );
}